import classNames from "classnames";
import {
  AnchorHTMLAttributes,
  MouseEventHandler,
  PureComponent,
  ReactChild,
} from "react";
import { Spinner } from "theme-ui";
import rightArrowUrl from "../../static/right-arrow.svg";
import "./Button.scss";
import { ViewAsPreservingLink } from "./ViewAsPreservingLink";

type Props = JSX.IntrinsicElements["button"] & {
  className?: string;
  disabled?: boolean;
  icon?: ReactChild;
  text: ReactChild;
  intent?: Intent;
  small?: boolean;
  loading?: boolean;
  onClick?: () => void;
};

export enum Intent {
  PRIMARY = "primary",
  PRIMARY_OUTLINE = "primary-outline",
  WHITE_OUTLINE = "white-outline",
  GRAY_OUTLINE = "gray-outline",
  BLACK_OUTLINE = "black-outline",

  SUCCESS = "success",
  LINK = "link",
  TRANSLUCENT = "translucent",
  MINIMAL = "minimal",
  NONE = "none",
  BLACK = "black",
  DANGER = "danger",
  ACTION = "action",
}

export default class Button extends PureComponent<Props> {
  public render(): JSX.Element {
    const {
      className,
      icon,
      text,
      type = "button",
      intent = Intent.NONE,
      small,
      ...otherProps
    } = this.props;
    return (
      <button
        {...otherProps}
        className={classNames(
          "button",
          `button-intent-${intent}`,
          small && "button-small",
          className,
          {
            disabled: this.props.disabled || this.props.loading,
          },
        )}
        type={type}
      >
        {icon && icon}
        <span style={{ opacity: this.props.loading ? 0 : undefined }}>
          {text}
        </span>
        <Spinner
          className="button-spinner"
          style={{ display: this.props.loading ? undefined : "none" }}
        />
      </button>
    );
  }
}

type ButtonLinkProps = JSX.IntrinsicElements["button"] & {
  to: string;
  icon?: ReactChild;
  text: ReactChild;
  className?: string;
  disabled?: boolean;
  external?: boolean;
  intent?: Intent;
  newTab?: boolean;
  onClick?: MouseEventHandler<Element>;
  small?: boolean;
  rightArrow?: boolean;
};

export class ButtonLink extends PureComponent<ButtonLinkProps> {
  public render(): JSX.Element {
    const {
      className,
      disabled,
      icon,
      text,
      intent = Intent.NONE,
      external,
      to,
      newTab,
      small,
      rightArrow,
      ...otherProps
    } = this.props;

    let textWithArrow = text;
    if (rightArrow) {
      textWithArrow = (
        <span className="legacy-d-flex legacy-align-items-center">
          {text} <img src={rightArrowUrl} className="legacy-ml-1" height={10} />
        </span>
      );
    }

    const buttonProps = {
      ...otherProps,
      className: classNames(
        "button",
        `button-intent-${intent}`,
        small && "button-small",
        className,
        {
          disabled,
        },
      ),
      children: textWithArrow,
    };
    const newTabProps: AnchorHTMLAttributes<HTMLAnchorElement> = {};
    if (newTab) {
      newTabProps.target = "_blank";
      newTabProps.rel = "noopener";
    }

    return disabled ? (
      <span {...buttonProps} />
    ) : external ? (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- FIXME
      <a href={to} {...(buttonProps as any)} {...newTabProps}>
        {icon && icon}
        {textWithArrow}
      </a>
    ) : (
      <ViewAsPreservingLink
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- FIXME
        {...(buttonProps as any)}
        {...newTabProps}
        to={to}
      >
        {icon && icon}
        {textWithArrow}
      </ViewAsPreservingLink>
    );
  }
}
